import React from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiRefreshCw, FiHeart } from 'react-icons/fi';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';
import { FormattedMovie } from '../types/movie';

interface FutureMoviesListProps {
  movies: FormattedMovie[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
}

const FutureMoviesList: React.FC<FutureMoviesListProps> = ({
  movies,
  loading,
  error,
  hasMore,
  loadMore,
}) => {
  const { user: isSignedIn } = useAuth();
  const { toggleFavorite, isFavorite } = useFavorites();
  const isAuthenticated = !!isSignedIn;

  const handleFavoriteClick = (e: React.MouseEvent, movieId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAuthenticated) {
      toggleFavorite(movieId);
    } else {
      console.log("User not signed in. Cannot add to favorites.");
    }
  };
  if (loading && !movies.length) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <h2 className="section-title">Future Releases</h2>
        <div className="flex items-center justify-center h-32">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin" />
            <div className="absolute inset-0 border-t-4 border-primary rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <h2 className="section-title">Future Releases</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-secondary-dark mb-4">
            Oops! We couldn't load the future releases at the moment.
          </p>
          <button 
            onClick={loadMore}
            className="inline-flex items-center space-x-2 btn-primary"
          >
            <FiRefreshCw className="w-5 h-5" />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  if (!movies.length) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <h2 className="section-title">Future Releases</h2>
        <div className="bg-background-light border border-primary/10 rounded-xl p-6 text-center">
          <p className="text-secondary-dark">
            No future releases found at this time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-8 animate-fade-in">
      <h2 className="section-title">Future Releases</h2>
      <div className="space-y-4">
        {movies.map((movie) => (
          <Link
            key={movie._id}
            to={`/movie/${movie.slug}`}
            className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-secondary-dark">
                  {movie.title}
                </h3>
                <p className="text-sm text-secondary-light">
                  {movie.releaseDate}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {isAuthenticated && (
                  <button
                    onClick={(e) => handleFavoriteClick(e, movie._id)}
                    className="p-2 text-secondary-light hover:text-red-500 transition-colors"
                    aria-label={isFavorite(movie._id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <FiHeart className={`w-5 h-5 ${isFavorite(movie._id) ? 'fill-red-500 text-red-500' : ''}`} />
                  </button>
                )}
                <FiChevronRight className="w-5 h-5 text-secondary-light" />
              </div>
            </div>
          </Link>
        ))}
      </div>
      
      {hasMore && (
        <div className="mt-6 text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <span>Load More</span>
                <FiChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default FutureMoviesList;